import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircleOutline';
import { REGISTRATION_PAYMENT_LABEL } from '../../components/payments/registrationPayment';
import { LeadConversionReadiness } from '../../types/lead';

interface ConvertToStudentConfirmDialogProps {
  open: boolean;
  leadName: string;
  readiness: LeadConversionReadiness | undefined;
  errorMessage: string | null;
  submitting: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

/** Convert-to-Student workflow correction: the ONLY UI for the actual
 * conversion action — deliberately just a confirmation summary, never the
 * old Passport/Father/Mother/Occupation form. That data already lives on
 * the Application (Student Application Wizard); this dialog only fires the
 * already-gated POST /students/:id/finalize once every readiness condition
 * is satisfied. */
export const ConvertToStudentConfirmDialog = ({
  open,
  leadName,
  readiness,
  errorMessage,
  submitting,
  onClose,
  onConfirm,
}: ConvertToStudentConfirmDialogProps) => {
  const fee = readiness?.registrationFee;
  const feeLabel =
    fee?.requiredAmount != null ? `${fee.currency?.symbol ?? fee.currency?.code ?? ''} ${fee.requiredAmount}` : 'Registration Fee';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Convert to Student</DialogTitle>
      <DialogContent>
        <Stack spacing={1.5} sx={{ mt: 1 }}>
          {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
          <Typography variant="subtitle1">{leadName}</Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <CheckCircleIcon fontSize="small" color="success" />
            <Typography variant="body2">Personal Appointment: Completed</Typography>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <CheckCircleIcon fontSize="small" color="success" />
            <Typography variant="body2">Application: Completed</Typography>
          </Stack>
          {fee?.paid ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <CheckCircleIcon fontSize="small" color="success" />
              <Typography variant="body2">Registration Fee: {feeLabel} Paid &amp; Confirmed</Typography>
            </Stack>
          ) : (
            <Alert severity="info">
              Registration Fee ({feeLabel}): {fee ? REGISTRATION_PAYMENT_LABEL[fee.registrationPaymentStatus] : 'Not Paid'}. The student can be registered
              now — the fee stays outstanding until a payment is recorded and approved by Finance.
            </Alert>
          )}
          <Typography variant="body2" sx={{ pt: 1 }}>
            Are you sure you want to convert this applicant to a Student?
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={submitting} onClick={onConfirm}>
          {submitting ? 'Converting…' : 'Convert to Student'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
